/**
 * Per-site KPI switches for one customer relationship (SITE-KPI-FLAGS).
 *
 * Whether this supplier is included in OTIF, OTD and NCR/PPM at ONE customer
 * site. The supplier-wide switches in KPI Settings sit above these: when one
 * of those is off, the supplier counts nowhere for that KPI, so the per-site
 * switch is shown off and locked with the reason.
 */

import { SupplierCustomerRelationship } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { Lock } from 'lucide-react';

/** The three KPI inclusion fields a relationship and a supplier profile share. */
export type KpiField = 'include_in_otif' | 'include_in_otd' | 'include_in_ncr_ppm';

/** The supplier-wide master switches, read from the supplier profile. */
export type KpiMasterFlags = Record<KpiField, boolean>;

interface RelationshipKpiSwitchesProps {
  relationship: SupplierCustomerRelationship;
  master: KpiMasterFlags;
  /** "PPM" or "PPK", the tenant's quality measure. */
  qualityLabel: string;
  /** An admin whose sites include this customer (or the platform); the API enforces it. */
  canEdit: boolean;
  saving: boolean;
  onChange: (field: KpiField, value: boolean) => void;
}

export function RelationshipKpiSwitches({
  relationship, master, qualityLabel, canEdit, saving, onChange,
}: RelationshipKpiSwitchesProps) {
  const kpis: { field: KpiField; label: string }[] = [
    { field: 'include_in_otif', label: 'OTIF' },
    { field: 'include_in_otd', label: 'OTD' },
    { field: 'include_in_ncr_ppm', label: `NCR/${qualityLabel}` },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {kpis.map(({ field, label }) => {
        const offEverywhere = !master[field];
        const checked = offEverywhere ? false : relationship[field] !== false;
        const id = `rel-${relationship.id}-${field}`;
        const title = offEverywhere
          ? `${label} is switched off for every site in KPI Settings`
          : `${checked ? 'Included' : 'Not included'} in ${label} at ${relationship.customer_name}`;
        return (
          <label key={field} htmlFor={id} title={title} className="flex items-center gap-1.5 text-sm whitespace-nowrap">
            <Switch
              id={id}
              checked={checked}
              disabled={!canEdit || saving || offEverywhere}
              onCheckedChange={(value) => onChange(field, value)}
              aria-label={title}
            />
            <span className={offEverywhere || !checked ? 'text-muted-foreground' : undefined}>{label}</span>
            {offEverywhere && <Lock className="h-3 w-3 text-muted-foreground" aria-hidden="true" />}
          </label>
        );
      })}
    </div>
  );
}
